import { Card, Icon, Loader, PageLayout, Typography } from '@bharatpe/bharat-ui';
import React, { useEffect, useRef, useState } from 'react';
import { getAppInfo, getURLParameter } from 'utils/nativeSupport';
import { CheckCircle, NavigateNext, WarningRounded, WatchLaterRounded } from '@bharatpe/bharat-ui/web/Icons';
import { TriggerNativeApiWrapper } from 'utils/nativeUpiHandler';
import { API_URL } from 'Services/CONSTANT';
import Toasty from 'components/atoms/Toasty';
import { formatDateDynamic, isEmpty } from 'utils/Utils';
import history from 'utils/History';
import AppHeader from 'components/atoms/AppHeader';
import UpiPoweredBy from 'components/atoms/UpiPoweredBy';
import './styles.scss';
import noTransactionsIcon from 'assets/images/autoPay/noTransactionsIcon.svg';
import { sendEvent } from 'utils/Sentry';
import { logAnalyticsEvent } from 'utils/AnalyticsEvent';
import { useLanguageConfig } from 'Slices/LanguageConfigSlice';

const TransactionList: React.FC = () => {
	const [transactionData, setTransactionData] = useState<any>();
	const [loading, setLoading] = useState(true);
	const observer = useRef();
	const { languageData } = useLanguageConfig();

	useEffect(() => {
		logAnalyticsEvent({
			trigger_point: 'Page View',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Txn_History_man',
			Module: 'Page',
			Action: 'Viewed',
		});
	}, []);

	const getTransactionList = async (withRefresh = false) => {
		const pageNumber = withRefresh ? 0 : transactionData?.page_no + 1;
		if (!withRefresh && transactionData?.page_no + 1 >= transactionData?.total_pages) return;
		try {
			const data: any = await TriggerNativeApiWrapper(
				`${API_URL.all_transactions}?txn_type=COLLECT%2CPAY&umn=${getURLParameter(
					window.location.href,
					'umn',
				)}&page_no=${pageNumber}&page_size=6`,
				'GET',
				{},
				getAppInfo() as any,
			);
			if (withRefresh) {
				setTransactionData({ ...data, loaded_count: 6 });
				setLoading(false);
				return;
			}
			setTransactionData({
				...transactionData,
				page_no: data?.page_no,
				loaded_count: transactionData?.loaded_count + data?.page_size,
				user_transactions: [...transactionData?.user_transactions, ...data?.user_transactions],
			});
			setLoading(false);
		} catch (err: any) {
			sendEvent(err);
			Toasty.error(err?.error || 'something went wrong');
		}
	};

	useEffect(() => {
		getTransactionList(true);
	}, []);

	const onTransactionClick = (transactionId: string) => {
		logAnalyticsEvent({
			trigger_point: 'Clicks on Details',
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Txn_History_man',
			Module: 'TxnDetail',
			Element: 'CTA',
			Action: 'Tapped',
			ctaType: 'detail',
		});
		history.push({
			pathname: '/upi/auto-pay-payment-status',
			search: `?transaction-id=${transactionId}`,
		});
	};

	const getTransactionStatusIcon = (status: string) => {
		switch (status) {
			case 'SUCCESS':
				return CheckCircle;
				break;
			case 'FAILED':
				return WarningRounded;
				break;
			case 'PENDING':
				return WatchLaterRounded;
		}
	};

	const getTransactionStatusClassName = (status: string) => {
		switch (status) {
			case 'SUCCESS':
				return 'success-logo';
				break;
			case 'FAILED':
				return 'failed-logo';
				break;
			case 'PENDING':
				return 'pending-logo';
		}
	};

	const lastItemRef = (node: any, id: number) => {
		if (observer.current) (observer.current as any).disconnect();
		(observer as any).current = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting) {
				getTransactionList();
			}
		});
		if (node) (observer as any).current.observe(node);
	};

	const backEventAction = () => {
		logAnalyticsEvent({
			trigger_point: `Clicks on Back Button`,
			Product: 'AutoPayUPI',
			Flow: 'Setting',
			Screen: 'Txn_History_man',
			Element: 'back',
			Action: 'Tapped',
			source: 'appHeader',
		});
	};

	return loading ? (
		<Loader loading />
	) : (
		<PageLayout className="white-background ">
			<>
				<AppHeader title={languageData.TRANSACTION_HISTORY} backEventAction={backEventAction} />
				<div className="mx5 mt6 text-center transaction-list">
					{isEmpty(transactionData?.user_transactions) && (
						<Card className="p4 mb5 mt5 flex flex-column flex-center mb4">
							<img src={noTransactionsIcon} />
							<Typography variant="Bold500">{languageData.NO_TRANSACTIONS}</Typography>
							<Typography variant="Light200" className="grey mt2">
								{languageData.YOU_HAVENT_MADE_ANY_TRANSACTION_YET}
							</Typography>
						</Card>
					)}
					{transactionData?.user_transactions &&
						transactionData?.user_transactions?.map((item: any, index: number) => (
							<div key={index} ref={(node: any) => lastItemRef(node, index)}>
								<Card
									className="p4 mb5 mt5 flex flex-column flex-center mb4"
									onClick={() => onTransactionClick(item.transaction_id)}>
									<div className="flex flex-row flex-justify flex-center width-100">
										<div className="flex flex-row flex-center">
											<img src={item?.payee_details?.payee_logo} className="circular-logo" />
											<div className="ml2 flex-start width-75">
												<Typography variant="Regular200" className="text-ellipsis width-100 text-left">
													{item?.payee_details?.name}
												</Typography>
												<Typography variant="Regular100" className="grey mt1 text-ellipsis width-100 text-left">
													{item?.payee_details?.vpa}
												</Typography>
											</div>
										</div>
										<div className="width-50">
											<div className="flex flex-column flex-end">
												<Typography variant="Regular200">₹{item?.amount}</Typography>
												<div className="flex flex-row flex-center">
													<Typography variant="Regular100">{languageData.DEBIT}</Typography>
													<img
														src={item?.payer_details?.bank_logo}
														loading="lazy"
														className="ml2"
														height={15}
														width={15}
													/>
												</div>
											</div>
										</div>
									</div>
									<div className="horizontal-dashed-divider my4"></div>
									<div className="flex flex-row flex-justify flex-center width-100">
										<div className="flex flex-row flex-center">
											<Icon
												icon={getTransactionStatusIcon(item?.status)}
												className={`${getTransactionStatusClassName(item?.status)} mr2`}
											/>
											<Typography variant="Regular200" className="grey">
												{formatDateDynamic(item?.created_at, `DD MMM' YYYY h:mm a`)}
											</Typography>
										</div>
										<div className="flex flex-row flex-center">
											<Typography variant="Regular200" className="underline primary">
												{languageData.DETAILS}
											</Typography>
											<Icon icon={NavigateNext} className="primary" />
										</div>
									</div>
								</Card>
							</div>
						))}
				</div>
				<div className="upi-powered-sticky">
					<UpiPoweredBy />
				</div>
			</>
		</PageLayout>
	);
};

export default TransactionList;
